import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  Select,
  FormControl,
  MenuItem,
  Snackbar,
  Alert,
  Slide,
  useMediaQuery,
  useTheme,
  alpha,
  Divider,
  Tooltip,
  LinearProgress,
  Fade,
  Zoom,
  CardActions,
  CircularProgress,
  Skeleton,
  Menu,
  ListItemIcon,
  ListItemText,
  Popover,
  AlertTitle,
  Badge,
  Stack,
  Paper as MuiPaper,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Autocomplete,
  Collapse,
  IconButton as MuiIconButton,
} from '@mui/material';

// ============================================
// ICON IMPORTS
// ============================================
import {
  Dashboard as DashboardIcon,
  ShoppingCart as ShoppingCartIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  LockOutlined as LockOutlinedIcon,
  Verified as VerifiedIcon,
  Refresh as RefreshIcon,
  ShoppingBag as ShoppingBagIcon,
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  StarOutline as StarOutlineIcon,
  AttachMoney as AttachMoneyIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
  Category as CategoryIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Store as StoreIcon,
  LocalShipping as LocalShippingIcon,
  Payment as PaymentIcon,
  Assessment as AssessmentIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
  Check as CheckIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LocalOffer as LocalOfferIcon,
  Place as PlaceIcon,
  PersonOutline as PersonOutlineIcon,
  CalendarToday as CalendarTodayIcon,
  Receipt as ReceiptIcon,
  Send as SendIcon,
  Menu as MenuIcon,
} from '@mui/icons-material';

// Import shared components
import Preloader from '../../theme/Preloader';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Footer from './components/Footer';
import { BRAND_COLORS, StatusChip } from '../buyers/components/styles';
import { formatCurrency, formatDate } from '../buyers/components/utils';

// ============================================
// ENHANCED COLOR SCHEME
// ============================================

const COLORS = {
  primary: '#1A237E',
  primaryLight: '#283593',
  primaryDark: '#0D1445',
  secondary: '#00BCD4',
  accent: '#FF6F00',
  success: '#2E7D32',
  warning: '#E65100',
  error: '#C62828',
  info: '#01579B',
  grey: {
    50: '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },
  status: {
    pending: '#E65100',
    processing: '#01579B',
    shipped: '#6A1B9A',
    delivered: '#2E7D32',
    cancelled: '#C62828',
  }
};

const getStatusColor = (status) => {
  const colors = {
    'pending': COLORS.status.pending,
    'processing': COLORS.status.processing,
    'shipped': COLORS.status.shipped,
    'delivered': COLORS.status.delivered,
    'cancelled': COLORS.status.cancelled,
    'refunded': '#4E342E',
    'on_hold': '#455A64',
  };
  return colors[status] || COLORS.grey[600];
};

const getStatusLabel = (status) => {
  const labels = {
    'pending': 'Pending',
    'processing': 'Processing',
    'shipped': 'Shipped',
    'delivered': 'Delivered',
    'cancelled': 'Cancelled',
    'refunded': 'Refunded',
    'on_hold': 'On Hold',
  };
  return labels[status] || status || 'N/A';
};

const getPaymentStatusColor = (status) => {
  const colors = {
    'paid': COLORS.success,
    'pending': COLORS.warning,
    'failed': COLORS.error,
    'refunded': '#4E342E',
  };
  return colors[status] || COLORS.grey[600];
};

const getPaymentStatusLabel = (status) => {
  const labels = {
    'paid': 'Paid',
    'pending': 'Pending',
    'failed': 'Failed',
    'refunded': 'Refunded',
  };
  return labels[status] || status || 'N/A';
};

// ============================================
// LOGGER
// ============================================

const logger = {
  info: (message, data) => console.log(`[SUPPLIER-ORDERS] ✅ ${message}`, data || ''),
  error: (message, error) => console.error(`[SUPPLIER-ORDERS] ❌ ${message}`, error || ''),
  debug: (message, data) => console.debug(`[SUPPLIER-ORDERS] 🔍 ${message}`, data || ''),
  warn: (message, data) => console.warn(`[SUPPLIER-ORDERS] ⚠️ ${message}`, data || ''),
};

// ============================================
// API CONFIGURATION
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

const API_ENDPOINTS = {
  ORDERS: {
    LIST: `${API_BASE_URL}/orders/orders.php`,
    UPDATE: (id) => `${API_BASE_URL}/orders/orders.php?id=${id}`,
  },
  PRODUCTS: {
    LIST: `${API_BASE_URL}/products/register.php`,
  },
  SUPPLIER: {
    PROFILE: `${API_BASE_URL}/suppliers/profile.php`,
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatDateShort = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return 'N/A';
  }
};

const formatTime = (date) => {
  if (!date) return 'N/A';
  try {
    const d = new Date(date);
    return d.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

// ============================================
// ENHANCED CUSTOM COMPONENTS
// ============================================

const OrderStatusBadge = ({ status, size = 'medium' }) => {
  const color = getStatusColor(status);
  const label = getStatusLabel(status);
  
  const icons = {
    'pending': <HourglassEmptyIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
    'processing': <CircularProgress size={size === 'small' ? 14 : 18} />,
    'shipped': <LocalShippingIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
    'delivered': <CheckCircleIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
    'cancelled': <CancelIcon sx={{ fontSize: size === 'small' ? 14 : 18 }} />,
  };
  
  return (
    <Chip
      icon={icons[status] || null}
      label={label}
      size={size}
      sx={{
        bgcolor: alpha(color, 0.12),
        color: color,
        fontWeight: 700,
        fontSize: size === 'small' ? '0.7rem' : '0.8rem',
        letterSpacing: '0.3px',
        border: `1px solid ${alpha(color, 0.15)}`,
        '& .MuiChip-icon': {
          color: color,
        },
        ...(size === 'small' && {
          height: 26,
          '& .MuiChip-label': {
            fontSize: '0.7rem',
            px: 1.5,
            fontWeight: 700,
          },
        }),
      }}
    />
  );
};

const StatCard = ({ icon, title, value, subtitle, color, trend, trendValue }) => {
  return (
    <Zoom in style={{ transitionDelay: '100ms' }}>
      <Card
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(color, 0.08)} 0%, ${alpha(color, 0.02)} 100%)`,
          border: `1px solid ${alpha(color, 0.12)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 12px 40px ${alpha(color, 0.15)}`,
            borderColor: alpha(color, 0.3),
          },
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: COLORS.grey[600],
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  fontSize: '0.65rem',
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800,
                  mt: 0.5,
                  color: color,
                  fontSize: { xs: '1.8rem', sm: '2rem' },
                  lineHeight: 1.2,
                }}
              >
                {value}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: COLORS.grey[600],
                    display: 'block',
                    mt: 0.25,
                    fontWeight: 500,
                  }}
                >
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  {trend === 'up' ? (
                    <ArrowUpwardIcon sx={{ fontSize: 14, color: COLORS.success }} />
                  ) : trend === 'down' ? (
                    <ArrowDownwardIcon sx={{ fontSize: 14, color: COLORS.error }} />
                  ) : null}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: trend === 'up' ? COLORS.success : trend === 'down' ? COLORS.error : COLORS.grey[600],
                      fontWeight: 700,
                    }}
                  >
                    {trendValue}
                  </Typography>
                </Box>
              )}
            </Box>
            <Avatar
              sx={{
                bgcolor: alpha(color, 0.12),
                color: color,
                width: 48,
                height: 48,
                border: `2px solid ${alpha(color, 0.15)}`,
                '& .MuiSvgIcon-root': { fontSize: 24 },
              }}
            >
              {icon}
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </Zoom>
  );
};

const OrderCard = ({ order, onView, onStatusUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  
  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  const handleStatusChange = (status) => {
    handleMenuClose();
    onStatusUpdate(order.id, status);
  };
  
  const statusColor = getStatusColor(order.status);
  
  return (
    <Fade in>
      <Card
        sx={{
          mb: 2.5,
          borderRadius: 4,
          overflow: 'hidden',
          border: `1px solid ${COLORS.grey[200]}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          background: '#FFFFFF',
          '&:hover': {
            boxShadow: '0 16px 48px rgba(0,0,0,0.08)',
            borderColor: COLORS.primary,
            transform: 'translateY(-2px)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            {/* Left section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
              <Avatar
                sx={{
                  bgcolor: alpha(COLORS.primary, 0.08),
                  color: COLORS.primary,
                  width: 48,
                  height: 48,
                  border: `2px solid ${alpha(COLORS.primary, 0.1)}`,
                }}
              >
                <ReceiptIcon />
              </Avatar>
              <Box>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={800} 
                  sx={{ 
                    color: COLORS.grey[900],
                    fontSize: '1rem',
                    letterSpacing: '-0.3px',
                  }}
                >
                  Order #{order.id}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                  <PersonOutlineIcon sx={{ fontSize: 14, color: COLORS.grey[500] }} />
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: COLORS.grey[700],
                      fontWeight: 600,
                    }}
                  >
                    {order.customer_name || 'Guest Customer'}
                  </Typography>
                  {order.customer_organisation && (
                    <>
                      <Typography variant="caption" color={COLORS.grey[400]}>•</Typography>
                      <Typography variant="caption" color={COLORS.grey[500]}>
                        {order.customer_organisation}
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </Box>
            
            {/* Right section */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <OrderStatusBadge status={order.status} />
              <Chip
                label={getPaymentStatusLabel(order.payment_status)}
                size="small"
                sx={{
                  bgcolor: alpha(getPaymentStatusColor(order.payment_status), 0.1),
                  color: getPaymentStatusColor(order.payment_status),
                  fontWeight: 700,
                  height: 26,
                  border: `1px solid ${alpha(getPaymentStatusColor(order.payment_status), 0.15)}`,
                  '& .MuiChip-label': { fontSize: '0.7rem', px: 1.5, fontWeight: 700 },
                }}
              />
              <IconButton
                size="small"
                onClick={handleMenuOpen}
                sx={{ 
                  bgcolor: COLORS.grey[100],
                  '&:hover': { bgcolor: COLORS.grey[200] },
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    minWidth: 200,
                    boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                    border: `1px solid ${COLORS.grey[200]}`,
                  },
                }}
              >
                <MenuItem onClick={() => { handleMenuClose(); onView(order); }} sx={{ fontWeight: 600 }}>
                  <ListItemIcon><VisibilityIcon fontSize="small" sx={{ color: COLORS.primary }} /></ListItemIcon>
                  <ListItemText primaryTypographyProps={{ fontWeight: 600 }}>View Details</ListItemText>
                </MenuItem>
                <Divider />
                <Typography variant="caption" sx={{ px: 2, py: 1, color: COLORS.grey[500], fontWeight: 700, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  Update Status
                </Typography>
                {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => {
                  const isActive = order.status === status;
                  const color = getStatusColor(status);
                  return (
                    <MenuItem 
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      selected={isActive}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: alpha(color, 0.08),
                        },
                        fontWeight: isActive ? 700 : 500,
                      }}
                    >
                      <ListItemIcon>
                        {isActive && <CheckIcon fontSize="small" sx={{ color }} />}
                      </ListItemIcon>
                      <ListItemText 
                        primaryTypographyProps={{ 
                          fontWeight: isActive ? 700 : 500,
                          color: isActive ? color : COLORS.grey[700],
                        }}
                      >
                        {getStatusLabel(status)}
                      </ListItemText>
                    </MenuItem>
                  );
                })}
              </Menu>
            </Box>
          </Box>
          
          {/* Order Details */}
          <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', mt: 2.5, pt: 2.5, borderTop: `1px solid ${COLORS.grey[100]}` }}>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                Total Amount
              </Typography>
              <Typography 
                variant="h5" 
                fontWeight={800} 
                sx={{ 
                  color: COLORS.primary,
                  fontSize: '1.25rem',
                  mt: 0.25,
                }}
              >
                {formatCurrency(order.total_amount || 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                Order Date
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25 }}>
                {formatDateShort(order.order_date)}
                <Typography component="span" variant="caption" sx={{ color: COLORS.grey[500], ml: 0.5, fontWeight: 500 }}>
                  {formatTime(order.order_date)}
                </Typography>
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                Delivery
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[800], mt: 0.25 }}>
                {order.delivery_method || 'Standard'}
              </Typography>
            </Box>
            {order.intended_use && (
              <Box sx={{ flex: 1, minWidth: 150 }}>
                <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                  Intended Use
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: COLORS.grey[700], mt: 0.25 }}>
                  {order.intended_use}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2, pt: 2, borderTop: `1px solid ${COLORS.grey[100]}` }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setExpanded(!expanded)}
              sx={{ 
                textTransform: 'none', 
                fontWeight: 600,
                color: COLORS.grey[600],
                '&:hover': { color: COLORS.primary },
              }}
            >
              {expanded ? 'Show Less' : 'Show More'}
            </Button>
            <Button
              size="small"
              variant="contained"
              startIcon={<VisibilityIcon />}
              onClick={() => onView(order)}
              sx={{ 
                borderRadius: 2, 
                textTransform: 'none',
                fontWeight: 700,
                ml: 1,
                background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.25)}`,
                '&:hover': {
                  boxShadow: `0 8px 24px ${alpha(COLORS.primary, 0.35)}`,
                  transform: 'translateY(-1px)',
                },
              }}
            >
              View Details
            </Button>
          </Box>
          
          {/* Expandable */}
          <Collapse in={expanded} sx={{ mt: 2 }}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[700], mb: 1.5 }}>
              Order Items
            </Typography>
            <Box sx={{ bgcolor: COLORS.grey[50], borderRadius: 2, p: 2.5, border: `1px solid ${COLORS.grey[200]}` }}>
              <Typography variant="body2" color={COLORS.grey[600]}>
                {order.intended_use ? `Intended Use: ${order.intended_use}` : 'No item details available'}
              </Typography>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </Fade>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const SupplierOrders = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  // ============================================
  // STATE
  // ============================================

  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailsDialog, setOrderDetailsDialog] = useState(false);
  const [updateStatusDialog, setUpdateStatusDialog] = useState(false);
  const [statusToUpdate, setStatusToUpdate] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(!isMobile);
  const [selectedTab, setSelectedTab] = useState('orders');
  const [userData, setUserData] = useState(null);
  const [supplierData, setSupplierData] = useState(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // UI states
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [viewMode, setViewMode] = useState('table');
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [changePasswordDialog, setChangePasswordDialog] = useState(false);

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processing: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    totalRevenue: 0,
  });

  // ============================================
  // API FUNCTIONS
  // ============================================

  const fetchSupplierData = useCallback(async () => {
    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) return;

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) return;

      const url = `${API_ENDPOINTS.SUPPLIER.PROFILE}?user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();
      
      if (result.success) {
        setSupplierData(result.data);
      }
    } catch (error) {
      logger.error('Error fetching supplier data:', error);
    }
  }, []);

  const fetchOrders = useCallback(async () => {
    logger.info('Fetching orders...');
    setLoading(true);

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }

      const url = `${API_ENDPOINTS.ORDERS.LIST}?supplier_id=${userId}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        let ordersData = [];

        if (Array.isArray(result.data)) {
          ordersData = result.data;
        } else if (result.data && typeof result.data === 'object') {
          if (Array.isArray(result.data.data)) {
            ordersData = result.data.data;
          } else if (Array.isArray(result.data.orders)) {
            ordersData = result.data.orders;
          } else {
            const arrayKey = Object.keys(result.data).find((key) => Array.isArray(result.data[key]));
            if (arrayKey) {
              ordersData = result.data[arrayKey];
            }
          }
        } else if (Array.isArray(result)) {
          ordersData = result;
        }

        setOrders(ordersData);
        calculateStats(ordersData);
        logger.info(`✅ Loaded ${ordersData.length} orders`);
      } else {
        throw new Error(result.message || 'Failed to fetch orders');
      }
    } catch (error) {
      logger.error('Error fetching orders:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to load orders. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const calculateStats = (ordersData) => {
    const statsData = {
      total: ordersData.length,
      pending: ordersData.filter(o => o.status === 'pending').length,
      processing: ordersData.filter(o => o.status === 'processing').length,
      shipped: ordersData.filter(o => o.status === 'shipped').length,
      delivered: ordersData.filter(o => o.status === 'delivered').length,
      cancelled: ordersData.filter(o => o.status === 'cancelled').length,
      totalRevenue: ordersData
        .filter(o => o.status === 'delivered')
        .reduce((sum, o) => sum + parseFloat(o.total_amount || 0), 0),
    };
    setStats(statsData);
  };

  const updateOrderStatus = useCallback(async (orderId, newStatus) => {
    if (!orderId || !newStatus) {
      setSnackbar({ open: true, message: 'Invalid order or status', severity: 'error' });
      return;
    }

    setSnackbar({ open: true, message: `Updating order status...`, severity: 'info' });

    try {
      const userDataStr = localStorage.getItem('user_data');
      if (!userDataStr) {
        throw new Error('User data not found. Please login again.');
      }

      const userData = JSON.parse(userDataStr);
      const userId = userData.id;

      const url = `${API_ENDPOINTS.ORDERS.UPDATE(orderId)}&user_id=${userId}`;
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          status: newStatus,
          user_id: userId,
          id: orderId
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        const updatedOrders = orders.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatus } 
            : order
        );
        setOrders(updatedOrders);
        calculateStats(updatedOrders);
        
        setOrderDetailsDialog(false);
        setUpdateStatusDialog(false);
        
        setSnackbar({ 
          open: true, 
          message: `Order #${orderId} status updated to ${getStatusLabel(newStatus)}`, 
          severity: 'success' 
        });
        
        setLastRefreshed(new Date());
      } else {
        throw new Error(result.message || 'Failed to update order status');
      }
    } catch (error) {
      logger.error('Error updating order status:', error);
      setSnackbar({ 
        open: true, 
        message: error.message || 'Failed to update order status', 
        severity: 'error' 
      });
    }
  }, [orders]);

  // ============================================
  // FILTERING & SORTING
  // ============================================

  useEffect(() => {
    let result = [...orders];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(order => 
        (order.id && order.id.toString().includes(term)) ||
        (order.customer_name && order.customer_name.toLowerCase().includes(term)) ||
        (order.customer_organisation && order.customer_organisation.toLowerCase().includes(term)) ||
        (order.session_id && order.session_id.toLowerCase().includes(term))
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(order => order.status === statusFilter);
    }

    if (paymentFilter !== 'all') {
      result = result.filter(order => order.payment_status === paymentFilter);
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      result = result.filter(order => {
        const orderDate = new Date(order.order_date);
        switch (dateFilter) {
          case 'today':
            return orderDate >= today;
          case 'week': {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return orderDate >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(today);
            monthAgo.setDate(monthAgo.getDate() - 30);
            return orderDate >= monthAgo;
          }
          default:
            return true;
        }
      });
    }

    switch (sortBy) {
      case 'newest':
        result.sort((a, b) => new Date(b.order_date) - new Date(a.order_date));
        break;
      case 'oldest':
        result.sort((a, b) => new Date(a.order_date) - new Date(b.order_date));
        break;
      case 'highest':
        result.sort((a, b) => parseFloat(b.total_amount) - parseFloat(a.total_amount));
        break;
      case 'lowest':
        result.sort((a, b) => parseFloat(a.total_amount) - parseFloat(b.total_amount));
        break;
      default:
        break;
    }

    setFilteredOrders(result);
  }, [orders, searchTerm, statusFilter, paymentFilter, dateFilter, sortBy]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const userType = localStorage.getItem('user_type');
    
    if (!token || userType !== 'supplier') {
      navigate('/RawAuth');
      return;
    }

    const userDataStr = localStorage.getItem('user_data');
    if (userDataStr) {
      try {
        setUserData(JSON.parse(userDataStr));
      } catch (e) {
        logger.error('Error parsing user data:', e);
      }
    }

    fetchSupplierData();
    fetchOrders();
  }, [fetchOrders, fetchSupplierData, navigate]);

  // ============================================
  // HANDLERS
  // ============================================
// ============================================
// NAVIGATION HANDLER
// ============================================
const handleSidebarNavigation = useCallback((path) => {
  // Update the selected tab state
  setSelectedTab(path);
  // Navigate to the path
  navigate(path);
  // Close drawer on mobile
  if (isMobile) setDrawerOpen(false);
}, [navigate, isMobile]);


  const handleLogout = useCallback(() => {
    localStorage.clear();
    navigate('/RawAuth');
    setSnackbar({ open: true, message: 'Logged out successfully', severity: 'info' });
  }, [navigate]);

  const handleChangePassword = useCallback(() => {
    setChangePasswordDialog(true);
    setProfileAnchorEl(null);
  }, []);

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDetailsDialog(true);
  };

  const handleRefresh = () => {
    fetchOrders();
    setSnackbar({ open: true, message: 'Orders refreshed', severity: 'info' });
  };

  const handleExport = () => {
    const headers = ['Order ID', 'Customer', 'Total Amount', 'Status', 'Payment Status', 'Order Date'];
    const rows = filteredOrders.map(order => [
      order.id,
      order.customer_name || 'N/A',
      order.total_amount || 0,
      getStatusLabel(order.status),
      getPaymentStatusLabel(order.payment_status),
      formatDate(order.order_date),
    ]);
    
    let csv = headers.join(',') + '\n';
    rows.forEach(row => {
      csv += row.join(',') + '\n';
    });
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    setSnackbar({ open: true, message: 'Orders exported successfully', severity: 'success' });
  };

  const handlePrint = () => {
    window.print();
  };

  // ============================================
  // RENDER FUNCTIONS
  // ============================================

  const renderStats = () => {
    const statItems = [
      { 
        icon: <ShoppingBagIcon />, 
        title: 'Total Orders', 
        value: stats.total,
        color: COLORS.primary,
      },
      { 
        icon: <HourglassEmptyIcon />, 
        title: 'Pending', 
        value: stats.pending,
        subtitle: `${((stats.pending / stats.total) * 100).toFixed(0)}% of total`,
        color: COLORS.status.pending,
        trend: stats.pending > 0 ? 'up' : null,
        trendValue: stats.pending > 0 ? `${stats.pending} awaiting` : 'All processed',
      },
      { 
        icon: <LocalShippingIcon />, 
        title: 'Processing', 
        value: stats.processing,
        subtitle: `${((stats.processing / stats.total) * 100).toFixed(0)}% of total`,
        color: COLORS.status.processing,
      },
      { 
        icon: <CheckCircleIcon />, 
        title: 'Delivered', 
        value: stats.delivered,
        subtitle: `${((stats.delivered / stats.total) * 100).toFixed(0)}% of total`,
        color: COLORS.status.delivered,
      },
      { 
        icon: <CancelIcon />, 
        title: 'Cancelled', 
        value: stats.cancelled,
        subtitle: `${((stats.cancelled / stats.total) * 100).toFixed(0)}% of total`,
        color: COLORS.status.cancelled,
      },
      { 
        icon: <AttachMoneyIcon />, 
        title: 'Revenue', 
        value: formatCurrency(stats.totalRevenue),
        subtitle: `From ${stats.delivered} delivered orders`,
        color: COLORS.accent,
      },
    ];

    return (
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        {statItems.map((item, index) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={index}>
            <StatCard {...item} />
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderFilters = () => (
    <Paper 
      sx={{ 
        p: 2.5, 
        mb: 3.5, 
        borderRadius: 4,
        border: `1px solid ${COLORS.grey[200]}`,
        background: '#FFFFFF',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        alignItems: 'center',
      }}
    >
      <TextField
        placeholder="Search orders by ID, customer..."
        size="small"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: COLORS.grey[500] }} />
            </InputAdornment>
          ),
          endAdornment: searchTerm && (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => setSearchTerm('')}>
                <ClearIcon fontSize="small" sx={{ color: COLORS.grey[500] }} />
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ 
          minWidth: 220, 
          flex: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover fieldset': {
              borderColor: COLORS.primary,
            },
          },
        }}
      />
      
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="all" sx={{ fontWeight: 600 }}>All Statuses</MenuItem>
          <MenuItem value="pending" sx={{ fontWeight: 500 }}>Pending</MenuItem>
          <MenuItem value="processing" sx={{ fontWeight: 500 }}>Processing</MenuItem>
          <MenuItem value="shipped" sx={{ fontWeight: 500 }}>Shipped</MenuItem>
          <MenuItem value="delivered" sx={{ fontWeight: 500 }}>Delivered</MenuItem>
          <MenuItem value="cancelled" sx={{ fontWeight: 500 }}>Cancelled</MenuItem>
        </Select>
      </FormControl>
      
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={paymentFilter}
          onChange={(e) => setPaymentFilter(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="all" sx={{ fontWeight: 600 }}>All Payments</MenuItem>
          <MenuItem value="paid" sx={{ fontWeight: 500 }}>Paid</MenuItem>
          <MenuItem value="pending" sx={{ fontWeight: 500 }}>Pending</MenuItem>
          <MenuItem value="failed" sx={{ fontWeight: 500 }}>Failed</MenuItem>
        </Select>
      </FormControl>
      
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="all" sx={{ fontWeight: 600 }}>All Time</MenuItem>
          <MenuItem value="today" sx={{ fontWeight: 500 }}>Today</MenuItem>
          <MenuItem value="week" sx={{ fontWeight: 500 }}>This Week</MenuItem>
          <MenuItem value="month" sx={{ fontWeight: 500 }}>This Month</MenuItem>
        </Select>
      </FormControl>
      
      <FormControl size="small" sx={{ minWidth: 150 }}>
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          displayEmpty
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            '& .MuiSelect-select': { py: 1.25 },
          }}
        >
          <MenuItem value="newest" sx={{ fontWeight: 600 }}>Newest First</MenuItem>
          <MenuItem value="oldest" sx={{ fontWeight: 500 }}>Oldest First</MenuItem>
          <MenuItem value="highest" sx={{ fontWeight: 500 }}>Highest Amount</MenuItem>
          <MenuItem value="lowest" sx={{ fontWeight: 500 }}>Lowest Amount</MenuItem>
        </Select>
      </FormControl>
      
      <Box sx={{ display: 'flex', gap: 1, ml: 'auto' }}>
        <Tooltip title="Refresh">
          <IconButton 
            onClick={handleRefresh} 
            size="small"
            sx={{ 
              bgcolor: COLORS.grey[100],
              borderRadius: 2,
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            <RefreshIcon sx={{ color: COLORS.grey[700] }} />
          </IconButton>
        </Tooltip>
     
        <Tooltip title="Print">
          <IconButton 
            onClick={handlePrint} 
            size="small"
            sx={{ 
              bgcolor: COLORS.grey[100],
              borderRadius: 2,
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            <PrintIcon sx={{ color: COLORS.grey[700] }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );

  const renderTable = () => (
    <TableContainer 
      component={Paper} 
      sx={{ 
        borderRadius: 4,
        border: `1px solid ${COLORS.grey[200]}`,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
      }}
    >
      <Table>
        <TableHead sx={{ bgcolor: COLORS.grey[50] }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Order ID
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Customer
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Amount
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Payment
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Date
            </TableCell>
            <TableCell sx={{ fontWeight: 800, color: COLORS.grey[700], fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredOrders.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                <Typography color={COLORS.grey[500]} fontWeight={600}>No orders found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            filteredOrders
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((order) => (
                <TableRow 
                  key={order.id}
                  hover
                  sx={{ 
                    '&:hover': { bgcolor: alpha(COLORS.primary, 0.02) },
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                  onClick={() => handleViewOrder(order)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.grey[900] }}>
                      #{order.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight={600} sx={{ color: COLORS.grey[800] }}>
                        {order.customer_name || 'Guest'}
                      </Typography>
                      {order.customer_organisation && (
                        <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 500 }}>
                          {order.customer_organisation}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: COLORS.primary }}>
                      {formatCurrency(order.total_amount || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <OrderStatusBadge status={order.status} size="small" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPaymentStatusLabel(order.payment_status)}
                      size="small"
                      sx={{
                        bgcolor: alpha(getPaymentStatusColor(order.payment_status), 0.1),
                        color: getPaymentStatusColor(order.payment_status),
                        fontWeight: 700,
                        height: 24,
                        border: `1px solid ${alpha(getPaymentStatusColor(order.payment_status), 0.15)}`,
                        '& .MuiChip-label': { fontSize: '0.65rem', px: 1.5, fontWeight: 700 },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: COLORS.grey[600] }}>
                      {formatDateShort(order.order_date)}
                      <br />
                      {formatTime(order.order_date)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                      <Tooltip title="View Details">
                        <IconButton 
                          size="small" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewOrder(order);
                          }}
                          sx={{ 
                            color: COLORS.primary,
                            bgcolor: alpha(COLORS.primary, 0.04),
                            '&:hover': { bgcolor: alpha(COLORS.primary, 0.12) },
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Status">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrder(order);
                            setUpdateStatusDialog(true);
                          }}
                          sx={{ 
                            color: COLORS.accent,
                            bgcolor: alpha(COLORS.accent, 0.04),
                            '&:hover': { bgcolor: alpha(COLORS.accent, 0.12) },
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: `1px solid ${COLORS.grey[200]}`,
          '& .MuiTablePagination-select': { fontWeight: 600 },
        }}
      />
    </TableContainer>
  );

  const renderCardView = () => (
    <Box>
      {filteredOrders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" sx={{ color: COLORS.grey[500], fontWeight: 600 }}>No orders found</Typography>
        </Box>
      ) : (
        filteredOrders
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              onView={handleViewOrder}
              onStatusUpdate={updateOrderStatus}
            />
          ))
      )}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        sx={{
          borderTop: `1px solid ${COLORS.grey[200]}`,
          bgcolor: '#FFFFFF',
          borderRadius: 3,
          mt: 2,
          '& .MuiTablePagination-select': { fontWeight: 600 },
        }}
      />
    </Box>
  );

  const renderOrderDetailsDialog = () => (
    <Dialog
      open={orderDetailsDialog}
      onClose={() => setOrderDetailsDialog(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4, 
          maxHeight: '90vh',
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      {selectedOrder && (
        <>
          <DialogTitle sx={{ p: 3, pb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h5" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
                  Order #{selectedOrder.id}
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.grey[500], fontWeight: 500, mt: 0.25 }}>
                  {formatDate(selectedOrder.order_date)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <OrderStatusBadge status={selectedOrder.status} />
                <IconButton 
                  onClick={() => setOrderDetailsDialog(false)}
                  sx={{ 
                    bgcolor: COLORS.grey[100],
                    '&:hover': { bgcolor: COLORS.grey[200] },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <PersonOutlineIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Customer Information
                  </Typography>
                  <Typography variant="body1" fontWeight={700} sx={{ color: COLORS.grey[900] }}>
                    {selectedOrder.customer_name || 'Guest Customer'}
                  </Typography>
                  {selectedOrder.customer_organisation && (
                    <Typography variant="body2" sx={{ color: COLORS.grey[600], fontWeight: 500 }}>
                      {selectedOrder.customer_organisation}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <ReceiptIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Order Summary
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Total Amount
                      </Typography>
                      <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.primary }}>
                        {formatCurrency(selectedOrder.total_amount || 0)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600, textTransform: 'uppercase', fontSize: '0.6rem', letterSpacing: '0.5px' }}>
                        Payment Status
                      </Typography>
                      <Chip
                        label={getPaymentStatusLabel(selectedOrder.payment_status)}
                        size="small"
                        sx={{
                          bgcolor: alpha(getPaymentStatusColor(selectedOrder.payment_status), 0.1),
                          color: getPaymentStatusColor(selectedOrder.payment_status),
                          fontWeight: 700,
                          mt: 0.5,
                          border: `1px solid ${alpha(getPaymentStatusColor(selectedOrder.payment_status), 0.15)}`,
                        }}
                      />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <LocalShippingIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Shipping Information
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                    <strong>Method:</strong> {selectedOrder.delivery_method || 'Standard'}
                  </Typography>
                  {selectedOrder.shipping_address && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: COLORS.grey[600] }}>
                      <strong>Address:</strong> {selectedOrder.shipping_address}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <ScheduleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Additional Information
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                    <strong>Order Date:</strong> {formatDate(selectedOrder.order_date)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.grey[700] }}>
                    <strong>Session:</strong> {selectedOrder.session_id || 'N/A'}
                  </Typography>
                  {selectedOrder.intended_use && (
                    <Typography variant="body2" sx={{ mt: 0.5, color: COLORS.grey[600] }}>
                      <strong>Intended Use:</strong> {selectedOrder.intended_use}
                    </Typography>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Paper sx={{ p: 2.5, bgcolor: COLORS.grey[50], borderRadius: 3, border: `1px solid ${COLORS.grey[200]}` }}>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: COLORS.grey[600], mb: 1.5, textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: '0.5px' }}>
                    <EditIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                    Update Status
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => {
                      const isActive = selectedOrder.status === status;
                      const color = getStatusColor(status);
                      return (
                        <Button
                          key={status}
                          size="small"
                          variant={isActive ? 'contained' : 'outlined'}
                          onClick={() => {
                            updateOrderStatus(selectedOrder.id, status);
                          }}
                          sx={{
                            borderRadius: 2,
                            textTransform: 'none',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            minWidth: 90,
                            ...(isActive && {
                              bgcolor: color,
                              color: '#FFFFFF',
                              boxShadow: `0 4px 12px ${alpha(color, 0.3)}`,
                              '&:hover': { 
                                bgcolor: color, 
                                opacity: 0.9,
                                boxShadow: `0 6px 20px ${alpha(color, 0.4)}`,
                              },
                              borderColor: color,
                            }),
                            ...(!isActive && {
                              borderColor: color,
                              color: color,
                              borderWidth: 2,
                              '&:hover': {
                                bgcolor: alpha(color, 0.05),
                                borderColor: color,
                              },
                            }),
                          }}
                        >
                          {isActive && <CheckIcon sx={{ fontSize: 14, mr: 0.5 }} />}
                          {getStatusLabel(status)}
                        </Button>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 2.5, pt: 2 }}>
            <Button 
              onClick={() => setOrderDetailsDialog(false)}
              sx={{ 
                fontWeight: 700,
                borderRadius: 2,
                px: 3,
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  const renderUpdateStatusDialog = () => (
    <Dialog
      open={updateStatusDialog}
      onClose={() => setUpdateStatusDialog(false)}
      maxWidth="xs"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4,
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
            Update Order Status
          </Typography>
          <IconButton 
            onClick={() => setUpdateStatusDialog(false)}
            sx={{ 
              bgcolor: COLORS.grey[100],
              '&:hover': { bgcolor: COLORS.grey[200] },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Typography variant="body2" sx={{ color: COLORS.grey[600], fontWeight: 500, mb: 2 }}>
          Order #{selectedOrder?.id} • Current: <strong style={{ color: getStatusColor(selectedOrder?.status) }}>{getStatusLabel(selectedOrder?.status)}</strong>
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {['pending', 'processing', 'shipped', 'delivered', 'cancelled'].map((status) => {
            const isActive = selectedOrder?.status === status;
            const color = getStatusColor(status);
            return (
              <Button
                key={status}
                variant={isActive ? 'contained' : 'outlined'}
                fullWidth
                onClick={() => {
                  if (selectedOrder) {
                    updateOrderStatus(selectedOrder.id, status);
                  }
                }}
                sx={{
                  justifyContent: 'flex-start',
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 700,
                  py: 1.5,
                  ...(isActive && {
                    bgcolor: color,
                    color: '#FFFFFF',
                    boxShadow: `0 4px 16px ${alpha(color, 0.3)}`,
                    '&:hover': { 
                      bgcolor: color, 
                      opacity: 0.9,
                      boxShadow: `0 6px 24px ${alpha(color, 0.4)}`,
                    },
                  }),
                  ...(!isActive && {
                    borderColor: color,
                    color: color,
                    borderWidth: 2,
                    '&:hover': {
                      bgcolor: alpha(color, 0.05),
                      borderColor: color,
                    },
                  }),
                }}
              >
                {isActive && <CheckIcon sx={{ mr: 1.5, color: '#FFFFFF' }} />}
                {getStatusLabel(status)}
              </Button>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button 
          onClick={() => setUpdateStatusDialog(false)}
          sx={{ 
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );

  const renderChangePasswordDialog = () => (
    <Dialog
      open={changePasswordDialog}
      onClose={() => setChangePasswordDialog(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{ 
        sx: { 
          borderRadius: 4,
          background: '#FFFFFF',
          boxShadow: '0 32px 64px rgba(0,0,0,0.15)',
        } 
      }}
    >
      <DialogTitle sx={{ p: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LockOutlinedIcon sx={{ color: COLORS.primary }} />
          <Typography variant="h6" fontWeight={800} sx={{ color: COLORS.grey[900] }}>
            Change Password
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3, pt: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
          <TextField 
            label="Current Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
          <TextField 
            label="New Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
          <TextField 
            label="Confirm New Password" 
            type="password" 
            fullWidth 
            size="medium"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: COLORS.primary,
                },
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, pt: 1 }}>
        <Button 
          onClick={() => setChangePasswordDialog(false)}
          sx={{ 
            fontWeight: 700,
            borderRadius: 2,
            px: 3,
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          sx={{ 
            bgcolor: COLORS.primary,
            fontWeight: 700,
            borderRadius: 2,
            px: 4,
            boxShadow: `0 4px 16px ${alpha(COLORS.primary, 0.3)}`,
            '&:hover': { 
              bgcolor: COLORS.primaryLight,
              boxShadow: `0 6px 24px ${alpha(COLORS.primary, 0.4)}`,
            },
          }}
        >
          Update Password
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ============================================
  // MAIN RENDER
  // ============================================

  if (loading && orders.length === 0) {
    return <Preloader text="Loading Orders..." size={60} fullScreen />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: COLORS.grey[50] }}>
     <Sidebar
  open={drawerOpen}
  onClose={() => setDrawerOpen(false)}
  isMobile={isMobile}
  selectedTab={selectedTab}
  onTabChange={handleSidebarNavigation}  // ← Use the handler instead of setSelectedTab
  userData={userData}
  onLogout={() => {
    localStorage.clear();
    navigate('/RawAuth');
  }}
/>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          width: { xs: '100%', md: 'calc(100% - 280px)' },
        }}
      >
        <Header
          isMobile={isMobile}
          onDrawerToggle={() => setDrawerOpen(true)}
          selectedTab={selectedTab}
          menuItems={[]}
          searchTerm={searchTerm}
          onSearchChange={(e) => setSearchTerm(e.target.value)}
          cartItemCount={0}
          onCartOpen={() => {}}
          notifications={[]}
          userData={userData}
          onLogout={handleLogout}
          onChangePassword={handleChangePassword}
       
        />

        <Box sx={{ flex: 1, p: { xs: 2, sm: 3 }, overflow: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 3.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography 
                  variant="h4" 
                  fontWeight={800} 
                  sx={{ 
                    color: COLORS.grey[900],
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                  }}
                >
                  <ShoppingBagIcon sx={{ color: COLORS.primary, fontSize: 32 }} />
                  Requests Management
                </Typography>
                <Typography variant="body2" sx={{ color: COLORS.grey[500], fontWeight: 500, mt: 0.25 }}>
                  {filteredOrders.length} orders found • Last updated: {lastRefreshed.toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefresh}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    fontWeight: 700,
                    borderColor: COLORS.grey[300],
                    color: COLORS.grey[700],
                    '&:hover': {
                      borderColor: COLORS.primary,
                      color: COLORS.primary,
                      bgcolor: alpha(COLORS.primary, 0.04),
                    },
                  }}
                >
                  Refresh
                </Button>
              
              </Box>
            </Box>
          </Box>

          {/* Stats */}
          {renderStats()}

          {/* Filters */}
          {renderFilters()}

          {/* View Mode Toggle */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant={viewMode === 'table' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('table')}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                  ...(viewMode === 'table' && {
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.25)}`,
                  }),
                }}
              >
                Table View
              </Button>
              <Button
                size="small"
                variant={viewMode === 'card' ? 'contained' : 'outlined'}
                onClick={() => setViewMode('card')}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  fontWeight: 700,
                  ...(viewMode === 'card' && {
                    background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryLight})`,
                    boxShadow: `0 4px 12px ${alpha(COLORS.primary, 0.25)}`,
                  }),
                }}
              >
                Card View
              </Button>
            </Box>
            <Typography variant="caption" sx={{ color: COLORS.grey[500], fontWeight: 600 }}>
              Showing {Math.min(filteredOrders.length, rowsPerPage)} of {filteredOrders.length} orders
            </Typography>
          </Box>

          {/* Orders List */}
          {viewMode === 'table' ? renderTable() : renderCardView()}

          {/* Dialogs */}
          {renderOrderDetailsDialog()}
          {renderUpdateStatusDialog()}
          {renderChangePasswordDialog()}
        </Box>

        <Footer />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            borderRadius: 3,
            boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
            fontWeight: 600,
            '& .MuiAlert-icon': { 
              alignItems: 'center',
              fontSize: 24,
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Global styles for animations */}
      <style>
        {`
          @keyframes pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.05); opacity: 0.8; }
          }
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
          ::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          ::-webkit-scrollbar-track {
            background: ${COLORS.grey[100]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: ${COLORS.grey[300]};
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: ${COLORS.grey[400]};
          }
          @media print {
            .MuiPaper-root {
              box-shadow: none !important;
              border: 1px solid ${COLORS.grey[300]} !important;
            }
            .MuiButton-root {
              display: none !important;
            }
            .MuiIconButton-root {
              display: none !important;
            }
            .MuiTablePagination-root {
              display: none !important;
            }
          }
        `}
      </style>
    </Box>
  );
};

export default SupplierOrders;